import SolutionsCaseStudyCTA from '@web/sections/Solutions/CaseStudyCTA'
import SolutionsChallenges from '@web/sections/Solutions/Challenges'
import SolutionsFooter from '@web/sections/Solutions/Footer'
import SolutionsHeader from '@web/sections/Solutions/Header'
import OtherSectors from '@web/sections/Solutions/OtherSectors'
import SolutionsRegulatoryContext from '@web/sections/Solutions/RegulatoryContext'
import SolutionsUseCases from '@web/sections/Solutions/UseCases'

export function LandUsePlanningPage() {
  return (
    <>
      <SolutionsHeader
        header="Land use planning and development through Resilience Explorer®"
        arcadeLink={{
          src: 'https://demo.arcade.software/rwp0tcNflT8uxaFe2wqA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
          title: 'How Resilience Explorer® Enables Adaptation Planning',
        }}
        subheader="Design tomorrow's growth for tomorrow's risk."
        descriptionLeft={
          <>
            <p>
              Resilience Explorer® helps you understand how climate and natural hazard risk affects
              land, development, and communities. It provides a consistent evidence base to support
              zoning decisions, spatial planning, and development assessment.
            </p>
          </>
        }
        descriptionRight={
          <p>
            The planning decisions you make about where communities grow, where infrastructure is
            built, and where development is appropriate will shape risk exposure for generations.
            Getting those calls right requires reliable, forward-looking information about where
            climate and natural hazard risks may increase and how development decisions influence
            future exposure.
          </p>
        }
      />
      <SolutionsChallenges
        header="Challenges facing Land Use Planning and Development"
        sections={[
          {
            title:
              'Making development decisions today that remain safe and defensible under future climate and natural hazard conditions',
            listitems: [],
          },
          {
            title:
              'Balancing development pressure with the need to avoid increasing long-term community risk',
            listitems: [],
          },
          {
            title:
              'Demonstrating that zoning and consent decisions are based on clear and credible evidence',
            listitems: [],
          },
        ]}
      />
      <SolutionsRegulatoryContext text="Planning policy now expects councils to direct growth away from high hazard risk and make risk-informed land use decisions. Resilience Explorer® helps you do both." />
      <SolutionsUseCases
        header="How Resilience Explorer® Enables Land Use Planning and Development"
        image={{
          src: '/website/asset_infrastructure_planning.jpg',
          alt: 'Land Use Planning and Development Header',
        }}
        accordions={[
          {
            title: 'Understand where development may face hazard risk',
            subtitle:
              'See where climate and natural hazard risks may affect current and future development areas.',
            content: (
              <>
                <p>
                  Without forward-looking hazard information, development decisions made today can
                  create exposure that only becomes visible years later.
                </p>
                <p className="mt-4">
                  Resilience Explorer® combines hazard data with land and property information to
                  show where flooding, coastal hazards, landslides, and other risks may affect land,
                  now and under future scenarios.
                </p>
                <p className="mt-4">
                  This means your team can see where development may face increasing exposure before
                  decisions are made, not after.
                </p>
              </>
            ),
          },
          {
            title: 'Support risk-informed zoning and spatial planning',
            subtitle: 'Use hazard information to guide long-term growth decisions.',
            content: (
              <>
                <p>
                  Guiding development toward safer locations requires hazard risk evidence that is
                  consistent, spatially explicit, and defensible across a whole district.
                </p>
                <p className="mt-4">
                  Resilience Explorer® shows how climate and natural hazard risk varies across your
                  district, giving you the evidence to support zone changes and plan modifications
                  with a clear, documented rationale.
                </p>
                <p className="mt-4">
                  This means spatial planning decisions are grounded in current evidence and easier
                  to explain and defend through formal processes.
                </p>
              </>
            ),
          },
          {
            title: 'Assess development proposals in hazard-prone areas',
            subtitle: 'Provide consistent risk information during development assessment.',
            content: (
              <>
                <p>
                  Inconsistent evidence across applications makes it difficult to maintain
                  consistency in consent decisions, and hard to explain those decisions clearly.
                </p>
                <p className="mt-4">
                  Resilience Explorer® gives your planning team a consistent evidence base to draw
                  on when assessing whether development should proceed in areas affected by climate
                  and natural hazard risks.
                </p>
                <p className="mt-4">
                  This means more consistent decisions, and a clearer basis for communicating them
                  to applicants and communities.
                </p>
              </>
            ),
          },
          {
            title: 'Understand how development decisions influence future risk',
            subtitle: 'See how planning choices affect long-term community exposure.',
            content: (
              <>
                <p>
                  Individual decisions that each seem reasonable in isolation can, taken together,
                  significantly increase future community exposure to climate and natural hazard
                  events.
                </p>
                <p className="mt-4">
                  Resilience Explorer® helps you understand those cumulative effects, showing how
                  land use decisions influence the number of people, buildings, and services exposed
                  over time.
                </p>
                <p className="mt-4">
                  This means your team can consider the long-term risk implications of growth
                  decisions, not just the immediate ones.
                </p>
              </>
            ),
          },
          {
            title: 'Support transparent planning decisions',
            subtitle: 'Provide evidence that supports planning processes and community discussion.',
            content: (
              <>
                <p>
                  Land use decisions are often contested, and communities and hearings panels expect
                  to understand the evidence behind them.
                </p>
                <p className="mt-4">
                  Resilience Explorer® provides maps, summaries, and structured outputs that help
                  you explain climate and natural hazard risk and planning decisions clearly during
                  consultation, hearings, and governance processes.
                </p>
                <p className="mt-4">
                  This means communities and decision makers can engage with the evidence rather
                  than the uncertainty.
                </p>
              </>
            ),
          },
        ]}
      />
      <SolutionsCaseStudyCTA header="Support land use decisions with clear, consistent climate and natural hazard risk evidence." />
      <OtherSectors />
      <SolutionsFooter />
    </>
  )
}
